import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2 } from "lucide-react";
import { SaleStockCompare } from "@/components/SaleStockCompare";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENT_SEASON } from "@/lib/season";

export const SALE_PROFILES = {
  solo_saldi: { label: "Solo saldi", range: "≥ 90% capi scontati", cls: "border-rose-500/50 text-rose-400" },
  soprattutto_saldi: { label: "Soprattutto saldi", range: "60 – 90%", cls: "border-orange-500/50 text-orange-400" },
  misto: { label: "Misto", range: "20 – 60%", cls: "border-amber-500/50 text-amber-400" },
  prezzo_pieno: { label: "Prezzo pieno", range: "< 20%", cls: "border-emerald-500/50 text-emerald-400" },
};

const Mix = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <p className="mb-2 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{title}</p>
    <div className="space-y-2">
      {items.slice(0, 10).map((i) => (
        <div key={i.name}>
          <div className="mb-0.5 flex justify-between text-xs"><span className="truncate">{i.name}</span><span className="text-muted-foreground">{num(i.value)} pz · {i.pct}%</span></div>
          <Progress value={i.pct} className="h-1.5" />
        </div>
      ))}
    </div>
  </div>
);

export const SaleCustomers = () => {
  const [season, setSeason] = useState(CURRENT_SEASON);
  const { data, isLoading } = useQuery({ queryKey: ["sale-customers", season], queryFn: async () => (await api.get("/sales/sale-customers", { params: { season } })).data });
  return (
    <Card className="mt-6 p-6" data-testid="sale-customers">
      <h3 className="font-display text-lg font-semibold">Clienti dei saldi · cosa comprano a prezzo scontato</h3>
      <p className="mb-4 text-xs text-muted-foreground">Profilo in base alla quota di capi comprati scontati (prezzo ribassato o sconto), al netto dei resi · solo clienti con almeno 3 capi · ordini dal 2026</p>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {Object.entries(SALE_PROFILES).map(([k, v]) => (
              <Link key={k} to={`/clienti?sale_profile=${k}`} data-testid={`sale-profile-${k}`}
                className="rounded-md border border-border/60 p-3 transition-colors hover:bg-secondary/50">
                <p className={`text-xs font-semibold ${v.cls.split(" ").pop()}`}>{v.label}</p>
                <p className="font-display text-xl font-bold" data-testid={`sale-profile-${k}-count`}>{num(data.profiles[k] || 0)}</p>
                <p className="text-[11px] text-muted-foreground">{v.range}</p>
              </Link>
            ))}
          </div>
          <p className="mb-3 text-xs text-muted-foreground" data-testid="sale-customers-summary">
            {num(data.sale_customers)} clienti "solo" o "soprattutto" saldi · {num(data.pieces)} capi comprati scontati</p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <Mix title="Taglie" items={data.sizes} testid="sale-sizes" />
            <Mix title="Categorie" items={data.categories} testid="sale-categories" />
            <Mix title="Colori" items={data.colors} testid="sale-colors" />
          </div>
          <div className="mt-8 border-t border-border/60 pt-6" data-testid="sale-stock-compare">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
              <h4 className="font-display text-base font-semibold">Saldi contro magazzino</h4>
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger className="w-44" data-testid="sale-stock-season"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutte">Tutte le stagioni</SelectItem>
                  {(data.seasons || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">
              <span className="text-primary">■</span> quota sugli acquisti in saldo · <span className="text-muted-foreground">■</span> quota sulla merce rimasta {season === "tutte" ? "(tutte le stagioni)" : season}
              · "Manca"/"In eccesso" se la differenza è di almeno {data.gap_threshold} punti</p>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <SaleStockCompare title="Taglie" rows={data.size_compare} testid="compare-sizes" />
              <SaleStockCompare title="Colori" rows={data.color_compare} testid="compare-colors" />
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
